import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import axios from 'axios';

import {
  CalendarDays,
  Coins,
  KeyRound,
  MapPin,
  RotateCcw,
  Send,
  UserPlus,
} from 'lucide-react';

import Pagination from '../../../shared/components/Pagination';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import {
  seedService,
} from '../../seed/services/seedService';

import {
  memberService,
} from '../../member/services/memberService';

import type {
  CreateAdminRequest,
  MemberInfo,
} from '../../member/types/member.types';

import {
  storeService,
} from '../../store/services/storeService';

import type {
  StoreListItem,
} from '../../store/types/store.types';

import {
  rankingService,
} from '../../ranking/services/rankingService';

import {
  attendanceService,
} from '../../attendance/services/attendanceService';

import type {
  AttendanceRankingItem,
  AttendanceRankingResponse,
} from '../../attendance/types/attendance.types';

type TabType =
  | 'point_minting'
  | 'store_point_minting'
  | 'admin_transfer'
  | 'admin_create'
  | 'score_reset'
  | 'password_reset'
  | 'attendance';

type ApiErrorPayload = {
  message?: string;
};

const ATTENDANCE_PAGE_SIZE =
  10;

const createInitialAdminForm =
  (): CreateAdminRequest => ({
    userId: '',
    password: '',
    userName: '',
    userNickname: '',
    storeId: 0,
    phoneNumber: '',
    point: 0,
  });

/**
 * 전화번호 자동 하이픈
 */
const formatPhoneNumber = (
  value: string,
): string => {
  const numbers =
    value.replace(
      /[^0-9]/g,
      '',
    );

  if (
    numbers.startsWith(
      '02',
    )
  ) {
    if (
      numbers.length <= 2
    ) {
      return numbers;
    }

    if (
      numbers.length <= 6
    ) {
      return `${numbers.slice(
        0,
        2,
      )}-${numbers.slice(2)}`;
    }

    return `${numbers.slice(
      0,
      2,
    )}-${numbers.slice(
      2,
      6,
    )}-${numbers.slice(
      6,
      10,
    )}`;
  }

  if (
    numbers.length <= 3
  ) {
    return numbers;
  }

  if (
    numbers.length <= 7
  ) {
    return `${numbers.slice(
      0,
      3,
    )}-${numbers.slice(3)}`;
  }

  return `${numbers.slice(
    0,
    3,
  )}-${numbers.slice(
    3,
    7,
  )}-${numbers.slice(
    7,
    11,
  )}`;
};

/**
 * API 전송용 전화번호
 */
const normalizePhoneNumber = (
  value: string,
): string => {
  return value.replace(
    /\D/g,
    '',
  );
};

const getErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (
    axios.isAxiosError(
      error,
    )
  ) {
    const payload =
      error.response?.data as
        | ApiErrorPayload
        | undefined;

    return (
      payload?.message ||
      error.message ||
      fallback
    );
  }

  if (
    error instanceof Error
  ) {
    return (
      error.message ||
      fallback
    );
  }

  return fallback;
};

const SuperAdminSystemManagePage:
  React.FC = () => {
  const modal =
    useModal();

  const [
    currentTab,
    setCurrentTab,
  ] =
    useState<TabType>(
      'point_minting',
    );

  const [
    loading,
    setLoading,
  ] = useState(false);

  /*
   * =========================
   * 라포이트 시드 발급
   * =========================
   */
  const [
    mintAmount,
    setMintAmount,
  ] = useState('');

  /*
   * =========================
   * 관리자 시드 지급
   * =========================
   */
  const [
    adminUsers,
    setAdminUsers,
  ] =
    useState<MemberInfo[]>(
      [],
    );

  const [
    selectedAdminId,
    setSelectedAdminId,
  ] =
    useState<number | ''>(
      '',
    );

  const [
    transferAmount,
    setTransferAmount,
  ] = useState('');

  const [
    loadingAdmins,
    setLoadingAdmins,
  ] = useState(false);

  /*
   * =========================
   * 매장 포인트
   * =========================
   */
  const [
    storePointMintStoreId,
    setStorePointMintStoreId,
  ] = useState(0);

  const [
    storePointMintAmount,
    setStorePointMintAmount,
  ] = useState('');

  /*
   * =========================
   * 승점 초기화
   * =========================
   */
  const [
    scoreResetStoreId,
    setScoreResetStoreId,
  ] = useState(0);

  /*
   * =========================
   * 비밀번호 초기화
   * =========================
   */
  const [
    passwordResetUserIdInput,
    setPasswordResetUserIdInput,
  ] = useState('');

  const [
    passwordResetTargetUser,
    setPasswordResetTargetUser,
  ] =
    useState<MemberInfo | null>(
      null,
    );

  const [
    loadingPasswordReset,
    setLoadingPasswordReset,
  ] = useState(false);

  /*
   * =========================
   * 출석
   * =========================
   */
  const now =
    useMemo(
      () => new Date(),
      [],
    );

  const [
    attendanceStoreId,
    setAttendanceStoreId,
  ] = useState(0);

  const [
    attendanceYear,
    setAttendanceYear,
  ] = useState(
    now.getFullYear(),
  );

  const [
    attendanceMonth,
    setAttendanceMonth,
  ] = useState(
    now.getMonth() + 1,
  );

  const [
    attendanceData,
    setAttendanceData,
  ] =
    useState<
      AttendanceRankingResponse | null
    >(null);

  const [
    attendancePage,
    setAttendancePage,
  ] = useState(1);

  const [
    attendanceLoading,
    setAttendanceLoading,
  ] = useState(false);

  /*
   * =========================
   * 매장
   * =========================
   */
  const [
    stores,
    setStores,
  ] =
    useState<
      StoreListItem[]
    >([]);

  const [
    loadingStores,
    setLoadingStores,
  ] = useState(false);

  /*
   * =========================
   * 관리자 생성
   * =========================
   */
  const [
    createForm,
    setCreateForm,
  ] =
    useState<CreateAdminRequest>(
      createInitialAdminForm,
    );

  /**
   * 매장 목록
   */
  const loadStores =
    useCallback(
      async (): Promise<void> => {
        try {
          setLoadingStores(
            true,
          );

          const data =
            await storeService
              .getStores();

          setStores(data);
        } catch (
          error: unknown
        ) {
          const message =
            getErrorMessage(
              error,
              '지점 목록 조회 실패',
            );

          await modal.alert(
            message,
          );
        } finally {
          setLoadingStores(
            false,
          );
        }
      },
      [modal],
    );

  /**
   * 관리자 목록
   */
  const loadAdminUsers =
    useCallback(
      async (): Promise<void> => {
        try {
          setLoadingAdmins(
            true,
          );

          const users =
            await memberService
              .getAdminMembers();

          setAdminUsers(
            users,
          );
        } catch (
          error: unknown
        ) {
          const message =
            getErrorMessage(
              error,
              '관리자 목록 조회 실패',
            );

          await modal.alert(
            message,
          );
        } finally {
          setLoadingAdmins(
            false,
          );
        }
      },
      [modal],
    );

  /**
   * 탭 변경에 따른
   * 필요한 데이터 조회
   */
  useEffect(() => {
    if (
      currentTab ===
      'admin_transfer'
    ) {
      void loadAdminUsers();

      return;
    }

    if (
      currentTab ===
        'admin_create' ||
      currentTab ===
        'store_point_minting' ||
      currentTab ===
        'score_reset' ||
      currentTab ===
        'attendance'
    ) {
      void loadStores();
    }
  }, [
    currentTab,
    loadAdminUsers,
    loadStores,
  ]);

  /**
   * 라포이트 시드 발행
   */
  const handleMintPoints =
    async (): Promise<void> => {
      const amount =
        Number(
          mintAmount,
        );

      if (
        !Number.isInteger(
          amount,
        ) ||
        amount <= 0
      ) {
        await modal.alert(
          '올바른 라포이트 시드를 입력해주세요.',
        );

        return;
      }

      const confirmed =
        await modal.confirm(
          `${amount.toLocaleString(
            'ko-KR',
          )} 라포이트 시드를 발급하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(true);

        await seedService
          .mintPoints(
            amount,
          );

        await modal.alert(
          '라포이트 시드가 발급되었습니다.',
        );

        setMintAmount('');
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '라포이트 시드 발급 실패',
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  /**
   * 관리자에게 시드 지급
   */
  const handleTransferPoints =
    async (): Promise<void> => {
      if (
        !selectedAdminId
      ) {
        await modal.alert(
          '관리자를 선택해주세요.',
        );

        return;
      }

      const amount =
        Number(
          transferAmount,
        );

      if (
        !Number.isInteger(
          amount,
        ) ||
        amount <= 0
      ) {
        await modal.alert(
          '올바른 라포이트 시드를 입력해주세요.',
        );

        return;
      }

      const selectedAdmin =
        adminUsers.find(
          (admin) =>
            admin.id ===
            selectedAdminId,
        );

      if (
        !selectedAdmin
      ) {
        await modal.alert(
          '관리자를 찾을 수 없습니다.',
        );

        return;
      }

      const confirmed =
        await modal.confirm(
          `${selectedAdmin.userName}(${selectedAdmin.userId})에게 ${amount.toLocaleString(
            'ko-KR',
          )} 라포이트 시드를 지급하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(true);

        await seedService
          .transferPointsToAdmin(
            selectedAdminId,
            amount,
          );

        await modal.alert(
          '라포이트 시드가 이관되었습니다.',
        );

        setSelectedAdminId(
          '',
        );

        setTransferAmount(
          '',
        );

        /*
         * 지급 후 관리자 포인트
         * 목록 갱신
         */
        await loadAdminUsers();
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '라포이트 시드 지급 실패',
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  /**
   * 매장 포인트 발행
   */
  const handleStorePointMint =
    async (): Promise<void> => {
      if (
        !storePointMintStoreId
      ) {
        await modal.alert(
          '지점을 선택해주세요.',
        );

        return;
      }

      const amount =
        Number(
          storePointMintAmount,
        );

      if (
        !Number.isInteger(
          amount,
        ) ||
        amount <= 0
      ) {
        await modal.alert(
          '올바른 매장 포인트를 입력해주세요.',
        );

        return;
      }

      const store =
        stores.find(
          (item) =>
            item.storeId ===
            storePointMintStoreId,
        );

      const confirmed =
        await modal.confirm(
          `${
            store?.storeName ??
            '선택한 지점'
          }에 ${amount.toLocaleString(
            'ko-KR',
          )} 매장 포인트를 발행하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(true);

        await storeService
          .mintStorePoints(
            storePointMintStoreId,
            amount,
          );

        await modal.alert(
          '매장 포인트가 발행되었습니다.',
        );

        setStorePointMintStoreId(
          0,
        );

        setStorePointMintAmount(
          '',
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '매장 포인트 발행 실패',
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  /**
   * 관리자 생성
   */
  const handleCreateAdmin =
    async (): Promise<void> => {
      if (
        !createForm.userId.trim()
      ) {
        await modal.alert(
          '아이디를 입력해주세요.',
        );

        return;
      }

      if (
        !createForm.password.trim()
      ) {
        await modal.alert(
          '비밀번호를 입력해주세요.',
        );

        return;
      }

      if (
        !createForm.userName.trim()
      ) {
        await modal.alert(
          '이름을 입력해주세요.',
        );

        return;
      }

      if (
        !createForm.userNickname.trim()
      ) {
        await modal.alert(
          '닉네임을 입력해주세요.',
        );

        return;
      }

      if (
        !createForm.storeId
      ) {
        await modal.alert(
          '지점을 선택해주세요.',
        );

        return;
      }

      if (
        !createForm.phoneNumber.trim()
      ) {
        await modal.alert(
          '전화번호를 입력해주세요.',
        );

        return;
      }

      const confirmed =
        await modal.confirm(
          `"${createForm.userName}" 관리자를 생성하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(true);

        await memberService
          .createAdmin({
            ...createForm,

            userId:
              createForm.userId.trim(),

            userName:
              createForm.userName.trim(),

            userNickname:
              createForm.userNickname.trim(),

            phoneNumber:
              normalizePhoneNumber(
                createForm.phoneNumber,
              ),
          });

        await modal.alert(
          '관리자가 생성되었습니다.',
        );

        setCreateForm(
          createInitialAdminForm(),
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '관리자 생성 실패',
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  /**
   * 스코어 초기화
   */
  const handleScoreReset =
    async (): Promise<void> => {
      if (
        !scoreResetStoreId
      ) {
        await modal.alert(
          '지점을 선택해주세요.',
        );

        return;
      }

      const store =
        stores.find(
          (item) =>
            item.storeId ===
            scoreResetStoreId,
        );

      const confirmed =
        await modal.confirm(
          `${
            store?.storeName ??
            '선택한 지점'
          }의 모든 회원 스코어를 초기화하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(true);

        await rankingService
          .resetStoreScore(
            scoreResetStoreId,
          );

        await modal.alert(
          '스코어가 초기화되었습니다.',
        );

        setScoreResetStoreId(
          0,
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '스코어 초기화 실패',
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  /**
   * 비밀번호 초기화 대상 회원 조회
   */
  const handlePasswordResetLookup =
    async (): Promise<void> => {
      const input =
        passwordResetUserIdInput.trim();

      if (!input) {
        await modal.alert(
          '아이디를 입력해주세요.',
        );

        return;
      }

      try {
        setLoadingPasswordReset(
          true,
        );

        const users =
          await memberService
            .getMembers();

        const found =
          users.find(
            (user) =>
              user.userId ===
              input,
          );

        if (!found) {
          setPasswordResetTargetUser(
            null,
          );

          await modal.alert(
            '해당 아이디의 사용자를 찾을 수 없습니다.',
          );

          return;
        }

        setPasswordResetTargetUser(
          found,
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '사용자 조회 실패',
          ),
        );
      } finally {
        setLoadingPasswordReset(
          false,
        );
      }
    };

  /**
   * 비밀번호 초기화 실행
   */
  const handlePasswordReset =
    async (): Promise<void> => {
      if (
        !passwordResetTargetUser
      ) {
        return;
      }

      const {
        userId,
        userName,
        phoneNumber,
      } =
        passwordResetTargetUser;

      if (!phoneNumber) {
        await modal.alert(
          '회원의 전화번호 정보가 없어 비밀번호를 초기화할 수 없습니다.',
        );

        return;
      }

      const confirmed =
        await modal.confirm(
          `${userName}(${userId})의 비밀번호를 초기화하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setLoading(true);

        await memberService
          .resetPasswordBySuperAdmin(
            userId
          );

        await modal.alert(
          '비밀번호가 초기화되었습니다.',
        );

        setPasswordResetUserIdInput(
          '',
        );

        setPasswordResetTargetUser(
          null,
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '비밀번호 초기화 실패',
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  /**
   * 출석 조회
   */
  const handleFetchAttendance =
    async (
      page: number = 1,
    ): Promise<void> => {
      if (
        !attendanceStoreId
      ) {
        await modal.alert(
          '지점을 선택해주세요.',
        );

        return;
      }

      try {
        setAttendanceLoading(
          true,
        );

        const data =
          await attendanceService
            .getAttendanceRanking(
              {
                storeId:
                  attendanceStoreId,

                year:
                  attendanceYear,

                month:
                  attendanceMonth,

                /*
                 * UI = 1부터
                 * API = 0부터
                 */
                page:
                  page - 1,

                size:
                  ATTENDANCE_PAGE_SIZE,
              },
            );

        setAttendanceData(
          data,
        );

        setAttendancePage(
          page,
        );
      } catch (
        error: unknown
      ) {
        await modal.alert(
          getErrorMessage(
            error,
            '출석 조회 실패',
          ),
        );
      } finally {
        setAttendanceLoading(
          false,
        );
      }
    };

  const attendanceDays =
    useMemo(
      () =>
        Array.from(
          {
            length:
              new Date(
                attendanceYear,
                attendanceMonth,
                0,
              ).getDate(),
          },
          (_, index) =>
            index + 1,
        ),
      [
        attendanceYear,
        attendanceMonth,
      ],
    );

  return (
    <div className="min-h-full w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
      {/* Header */}
      <div className="mb-5 sm:mb-6">
        <h1 className="mb-2 text-2xl font-semibold text-gray-900 sm:text-3xl">
          시스템 관리
        </h1>

        <p className="max-w-3xl break-keep text-sm leading-6 text-gray-600 sm:text-base">
          라포이트 시드, 관리자 계정,
          지점 포인트 및 운영 설정을
          관리합니다.
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-5 overflow-x-auto border-b border-gray-200 sm:mb-6">
        <div className="flex min-w-max gap-1 sm:gap-2">
          <button
            type="button"
            onClick={() =>
              setCurrentTab(
                'point_minting',
              )
            }
            className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 sm:text-base ${
              currentTab ===
              'point_minting'
                ? 'border-b-2 border-main text-main'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <Coins
                size={18}
              />

              라포이트 시드 발급
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentTab(
                'store_point_minting',
              )
            }
            className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 sm:text-base ${
              currentTab ===
              'store_point_minting'
                ? 'border-b-2 border-main text-main'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <MapPin
                size={18}
              />

              매장 포인트 발행
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentTab(
                'attendance',
              )
            }
            className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 sm:text-base ${
              currentTab ===
              'attendance'
                ? 'border-b-2 border-main text-main'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <CalendarDays
                size={18}
              />

              출석 조회
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentTab(
                'admin_transfer',
              )
            }
            className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 sm:text-base ${
              currentTab ===
              'admin_transfer'
                ? 'border-b-2 border-main text-main'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <Send
                size={18}
              />

              관리자 라포이트 시드 지급
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentTab(
                'admin_create',
              )
            }
            className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 sm:text-base ${
              currentTab ===
              'admin_create'
                ? 'border-b-2 border-main text-main'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <UserPlus
                size={18}
              />

              관리자 생성
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentTab(
                'score_reset',
              )
            }
            className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 sm:text-base ${
              currentTab ===
              'score_reset'
                ? 'border-b-2 border-main text-main'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <RotateCcw
                size={18}
              />

              스코어 초기화
            </span>
          </button>

          <button
            type="button"
            onClick={() =>
              setCurrentTab(
                'password_reset',
              )
            }
            className={`shrink-0 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors sm:px-4 sm:text-base ${
              currentTab ===
              'password_reset'
                ? 'border-b-2 border-main text-main'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span className="flex items-center gap-2">
              <KeyRound
                size={18}
              />

              비밀번호 초기화
            </span>
          </button>
        </div>
      </div>

      {/* Contents */}
      <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
        {/* 시드 발급 */}
        {currentTab ===
          'point_minting' && (
          <div className="min-w-0">
            <h2 className="mb-3 text-lg font-semibold text-gray-900 sm:mb-4 sm:text-xl">
              라포이트 시드 발급
            </h2>

            <p className="mb-5 text-sm leading-6 text-gray-600">
              시스템에 새로운 라포이트
              시드를 생성합니다.
            </p>

            <div className="w-full max-w-md">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                발급할 라포이트 시드
              </label>

              <input
                type="number"
                value={mintAmount}
                onChange={(
                  event,
                ) =>
                  setMintAmount(
                    event.target
                      .value,
                  )
                }
                placeholder="라포이트 시드 입력"
                disabled={loading}
                min="1"
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-main focus:ring-2 focus:ring-main/10"
              />

              <button
                type="button"
                onClick={() =>
                  void handleMintPoints()
                }
                disabled={
                  loading ||
                  !mintAmount
                }
                className="mt-4 w-full rounded-lg bg-main px-5 py-3 text-sm font-medium text-white transition hover:bg-main-medium disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading
                  ? '발급 중...'
                  : '발급하기'}
              </button>
            </div>
          </div>
        )}

        {/* 매장 포인트 */}
        {currentTab ===
          'store_point_minting' && (
          <div>
            <h2 className="mb-3 text-xl font-semibold">
              매장 포인트 발행
            </h2>

            {loadingStores ? (
              <div className="py-8 text-center text-gray-500">
                지점 목록을 불러오는 중...
              </div>
            ) : (
              <div className="w-full max-w-md">
                <label className="mb-2 block text-sm font-medium">
                  지점 선택
                </label>

                <select
                  value={
                    storePointMintStoreId
                  }
                  onChange={(
                    event,
                  ) =>
                    setStorePointMintStoreId(
                      Number(
                        event.target
                          .value,
                      ),
                    )
                  }
                  disabled={loading}
                  className="mb-4 w-full rounded-lg border border-gray-300 px-4 py-3"
                >
                  <option value={0}>
                    지점을 선택하세요
                  </option>

                  {stores.map(
                    (store) => (
                      <option
                        key={
                          store.storeId
                        }
                        value={
                          store.storeId
                        }
                      >
                        {
                          store.storeName
                        }
                      </option>
                    ),
                  )}
                </select>

                <label className="mb-2 block text-sm font-medium">
                  발행할 매장 포인트
                </label>

                <input
                  type="number"
                  min="1"
                  value={
                    storePointMintAmount
                  }
                  onChange={(
                    event,
                  ) =>
                    setStorePointMintAmount(
                      event.target
                        .value,
                    )
                  }
                  disabled={loading}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3"
                />

                <button
                  type="button"
                  onClick={() =>
                    void handleStorePointMint()
                  }
                  disabled={
                    loading ||
                    !storePointMintStoreId ||
                    !storePointMintAmount
                  }
                  className="mt-4 w-full rounded-lg bg-main px-5 py-3 font-medium text-white disabled:opacity-50"
                >
                  {loading
                    ? '발행 중...'
                    : '발행하기'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* 관리자 시드 */}
        {currentTab ===
          'admin_transfer' && (
          <div>
            <h2 className="mb-4 text-xl font-semibold">
              관리자 라포이트 시드 지급
            </h2>

            {loadingAdmins ? (
              <div className="py-8 text-center text-gray-500">
                관리자 목록을 불러오는 중...
              </div>
            ) : (
              <div className="max-w-md">
                <select
                  value={
                    selectedAdminId
                  }
                  onChange={(
                    event,
                  ) =>
                    setSelectedAdminId(
                      event.target
                        .value
                        ? Number(
                            event
                              .target
                              .value,
                          )
                        : '',
                    )
                  }
                  className="mb-4 w-full rounded-lg border border-gray-300 px-4 py-3"
                >
                  <option value="">
                    관리자를 선택하세요
                  </option>

                  {adminUsers.map(
                    (admin) => (
                      <option
                        key={
                          admin.id
                        }
                        value={
                          admin.id
                        }
                      >
                        {
                          admin.userName
                        }{' '}
                        (
                        {
                          admin.userId
                        }
                        ) -{' '}
                        {admin.point.toLocaleString(
                          'ko-KR',
                        )}
                        P
                      </option>
                    ),
                  )}
                </select>

                <input
                  type="number"
                  min="1"
                  value={
                    transferAmount
                  }
                  onChange={(
                    event,
                  ) =>
                    setTransferAmount(
                      event.target
                        .value,
                    )
                  }
                  placeholder="지급할 시드"
                  className="w-full rounded-lg border border-gray-300 px-4 py-3"
                />

                <button
                  type="button"
                  onClick={() =>
                    void handleTransferPoints()
                  }
                  disabled={
                    loading ||
                    !selectedAdminId ||
                    !transferAmount
                  }
                  className="mt-4 w-full rounded-lg bg-main px-5 py-3 text-white disabled:opacity-50"
                >
                  {loading
                    ? '지급 중...'
                    : '지급하기'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* 관리자 생성 */}
        {currentTab ===
          'admin_create' && (
          <div>
            <h2 className="mb-4 text-xl font-semibold">
              관리자 생성
            </h2>

            <div className="max-w-md space-y-4">
              <input
                value={
                  createForm.userId
                }
                onChange={(
                  event,
                ) =>
                  setCreateForm(
                    (
                      previous,
                    ) => ({
                      ...previous,
                      userId:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="아이디"
                className="w-full rounded-lg border px-4 py-3"
              />

              <input
                type="password"
                value={
                  createForm.password
                }
                onChange={(
                  event,
                ) =>
                  setCreateForm(
                    (
                      previous,
                    ) => ({
                      ...previous,
                      password:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="비밀번호"
                className="w-full rounded-lg border px-4 py-3"
              />

              <input
                value={
                  createForm.userName
                }
                onChange={(
                  event,
                ) =>
                  setCreateForm(
                    (
                      previous,
                    ) => ({
                      ...previous,
                      userName:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="이름"
                className="w-full rounded-lg border px-4 py-3"
              />

              <input
                value={
                  createForm.userNickname
                }
                onChange={(
                  event,
                ) =>
                  setCreateForm(
                    (
                      previous,
                    ) => ({
                      ...previous,
                      userNickname:
                        event
                          .target
                          .value,
                    }),
                  )
                }
                placeholder="닉네임"
                className="w-full rounded-lg border px-4 py-3"
              />

              <select
                value={
                  createForm.storeId
                }
                onChange={(
                  event,
                ) =>
                  setCreateForm(
                    (
                      previous,
                    ) => ({
                      ...previous,
                      storeId:
                        Number(
                          event
                            .target
                            .value,
                        ),
                    }),
                  )
                }
                className="w-full rounded-lg border px-4 py-3"
              >
                <option value={0}>
                  지점을 선택하세요
                </option>

                {stores.map(
                  (store) => (
                    <option
                      key={
                        store.storeId
                      }
                      value={
                        store.storeId
                      }
                    >
                      {
                        store.storeName
                      }
                    </option>
                  ),
                )}
              </select>

              <input
                value={
                  createForm.phoneNumber
                }
                onChange={(
                  event,
                ) =>
                  setCreateForm(
                    (
                      previous,
                    ) => ({
                      ...previous,

                      phoneNumber:
                        formatPhoneNumber(
                          event
                            .target
                            .value,
                        ),
                    }),
                  )
                }
                placeholder="010-1234-5678"
                className="w-full rounded-lg border px-4 py-3"
              />

              <input
                type="number"
                min="0"
                value={
                  createForm.point
                }
                onChange={(
                  event,
                ) =>
                  setCreateForm(
                    (
                      previous,
                    ) => ({
                      ...previous,

                      point:
                        Number(
                          event
                            .target
                            .value,
                        ),
                    }),
                  )
                }
                placeholder="초기 라포이트 시드"
                className="w-full rounded-lg border px-4 py-3"
              />

              <button
                type="button"
                onClick={() =>
                  void handleCreateAdmin()
                }
                disabled={loading}
                className="w-full rounded-lg bg-main px-5 py-3 text-white disabled:opacity-50"
              >
                {loading
                  ? '생성 중...'
                  : '관리자 생성'}
              </button>
            </div>
          </div>
        )}

        {/* Score reset */}
        {currentTab ===
          'score_reset' && (
          <div>
            <h2 className="mb-4 text-xl font-semibold">
              지점별 스코어 초기화
            </h2>

            <div className="max-w-md">
              <select
                value={
                  scoreResetStoreId
                }
                onChange={(
                  event,
                ) =>
                  setScoreResetStoreId(
                    Number(
                      event.target
                        .value,
                    ),
                  )
                }
                className="w-full rounded-lg border px-4 py-3"
              >
                <option value={0}>
                  지점을 선택하세요
                </option>

                {stores.map(
                  (store) => (
                    <option
                      key={
                        store.storeId
                      }
                      value={
                        store.storeId
                      }
                    >
                      {
                        store.storeName
                      }
                    </option>
                  ),
                )}
              </select>

              <button
                type="button"
                onClick={() =>
                  void handleScoreReset()
                }
                disabled={
                  loading ||
                  !scoreResetStoreId
                }
                className="mt-4 w-full rounded-lg bg-red-500 px-6 py-3 text-white hover:bg-red-600 disabled:opacity-50"
              >
                {loading
                  ? '초기화 중...'
                  : '스코어 초기화'}
              </button>
            </div>
          </div>
        )}

        {/* Attendance */}
        {currentTab ===
          'attendance' && (
          <div>
            <h2 className="mb-4 text-xl font-semibold">
              출석 랭킹 조회
            </h2>

            <div className="mb-6 flex flex-wrap items-end gap-4">
              <select
                value={
                  attendanceStoreId
                }
                onChange={(
                  event,
                ) => {
                  setAttendanceStoreId(
                    Number(
                      event.target
                        .value,
                    ),
                  );

                  setAttendanceData(
                    null,
                  );

                  setAttendancePage(
                    1,
                  );
                }}
                className="rounded-lg border px-4 py-3"
              >
                <option value={0}>
                  지점 선택
                </option>

                {stores.map(
                  (store) => (
                    <option
                      key={
                        store.storeId
                      }
                      value={
                        store.storeId
                      }
                    >
                      {
                        store.storeName
                      }
                    </option>
                  ),
                )}
              </select>

              <select
                value={
                  attendanceYear
                }
                onChange={(
                  event,
                ) => {
                  setAttendanceYear(
                    Number(
                      event.target
                        .value,
                    ),
                  );

                  setAttendanceData(
                    null,
                  );

                  setAttendancePage(
                    1,
                  );
                }}
                className="rounded-lg border px-4 py-3"
              >
                {[
                  now.getFullYear() -
                    1,
                  now.getFullYear(),
                  now.getFullYear() +
                    1,
                ].map(
                  (year) => (
                    <option
                      key={year}
                      value={year}
                    >
                      {year}년
                    </option>
                  ),
                )}
              </select>

              <select
                value={
                  attendanceMonth
                }
                onChange={(
                  event,
                ) => {
                  setAttendanceMonth(
                    Number(
                      event.target
                        .value,
                    ),
                  );

                  setAttendanceData(
                    null,
                  );

                  setAttendancePage(
                    1,
                  );
                }}
                className="rounded-lg border px-4 py-3"
              >
                {Array.from(
                  {
                    length: 12,
                  },
                  (_, index) =>
                    index + 1,
                ).map(
                  (month) => (
                    <option
                      key={month}
                      value={month}
                    >
                      {month}월
                    </option>
                  ),
                )}
              </select>

              <button
                type="button"
                onClick={() =>
                  void handleFetchAttendance(
                    1,
                  )
                }
                disabled={
                  attendanceLoading ||
                  !attendanceStoreId
                }
                className="rounded-lg bg-main px-6 py-3 text-white disabled:opacity-50"
              >
                {attendanceLoading
                  ? '조회 중...'
                  : '조회'}
              </button>
            </div>

            {attendanceData && (
              <>
                {attendanceData
                  .rankings
                  .length ===
                0 ? (
                  <div className="py-12 text-center text-gray-500">
                    출석 내역이 없습니다.
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto rounded-xl border">
                    <table className="min-w-[860px] w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="border-b border-r px-3 py-3 text-left">
                            닉네임
                          </th>

                          <th className="border-b border-r px-3 py-3 text-center">
                            출석수
                          </th>

                          {attendanceDays.map(
                            (day) => (
                              <th
                                key={
                                  day
                                }
                                className="w-8 border-b border-r px-2 py-3 text-center"
                              >
                                {day}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {attendanceData.rankings.map(
                          (
                            item: AttendanceRankingItem,
                          ) => (
                            <tr
                              key={
                                item.userId
                              }
                              className="hover:bg-gray-50"
                            >
                              <td className="border-b border-r px-3 py-3">
                                {
                                  item.nickname
                                }
                              </td>

                              <td className="border-b border-r px-3 py-3 text-center">
                                {
                                  item.attendanceCount
                                }
                              </td>

                              {attendanceDays.map(
                                (
                                  day,
                                ) => {
                                  const dateKey =
                                    `${attendanceMonth}/${day}`;

                                  return (
                                    <td
                                      key={
                                        dateKey
                                      }
                                      className="w-8 border-b border-r px-2 py-3 text-center"
                                    >
                                      {item
                                        .attendanceByDate[
                                        dateKey
                                      ] ? (
                                        <span className="font-bold text-main">
                                          O
                                        </span>
                                      ) : null}
                                    </td>
                                  );
                                },
                              )}
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={
                    attendancePage
                  }
                  totalItems={
                    attendanceData
                      .totalCount
                  }
                  itemsPerPage={
                    ATTENDANCE_PAGE_SIZE
                  }
                  onPageChange={(
                    page,
                  ) =>
                    void handleFetchAttendance(
                      page,
                    )
                  }
                />
              </>
            )}
          </div>
        )}

        {/* Password reset */}
        {currentTab ===
          'password_reset' && (
          <div>
            <h2 className="mb-4 text-xl font-semibold">
              회원 비밀번호 초기화
            </h2>

            <div className="max-w-md">
              <div className="mb-4 flex gap-2">
                <input
                  value={
                    passwordResetUserIdInput
                  }
                  onChange={(
                    event,
                  ) => {
                    setPasswordResetUserIdInput(
                      event.target
                        .value,
                    );

                    setPasswordResetTargetUser(
                      null,
                    );
                  }}
                  placeholder="회원 아이디"
                  className="min-w-0 flex-1 rounded-lg border px-4 py-3"
                />

                <button
                  type="button"
                  onClick={() =>
                    void handlePasswordResetLookup()
                  }
                  disabled={
                    loadingPasswordReset ||
                    !passwordResetUserIdInput.trim()
                  }
                  className="rounded-lg bg-main px-5 text-white disabled:opacity-50"
                >
                  {loadingPasswordReset
                    ? '조회 중'
                    : '조회'}
                </button>
              </div>

              {passwordResetTargetUser && (
                <div className="mb-4 space-y-1 rounded-lg bg-gray-50 p-4 text-sm">
                  <p>
                    이름:{' '}
                    {
                      passwordResetTargetUser.userName
                    }
                  </p>

                  <p>
                    아이디:{' '}
                    {
                      passwordResetTargetUser.userId
                    }
                  </p>

                  <p>
                    전화번호:{' '}
                    {
                      passwordResetTargetUser.phoneNumber ??
                      '-'
                    }
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={() =>
                  void handlePasswordReset()
                }
                disabled={
                  loading ||
                  !passwordResetTargetUser
                }
                className="w-full rounded-lg bg-red-500 px-6 py-3 text-white disabled:opacity-50"
              >
                {loading
                  ? '초기화 중...'
                  : '비밀번호 초기화'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminSystemManagePage;